import { useLanguage } from "@/contexts/LanguageContext";
import { useLocation } from "react-router-dom";

/**
 * Hook to get localized paths based on current language
 */
export const useLocalizedPath = () => {
  const { language } = useLanguage();
  const location = useLocation();

  /**
   * Get localized path - adds /en prefix for English, no prefix for Thai
   */
  const getLocalizedPath = (path: string): string => {
    // Remove any existing /en prefix first
    const cleanPath = path.replace(/^\/en/, "");
    
    if (language === "en") {
      // For root path, return /en
      if (cleanPath === "" || cleanPath === "/") {
        return "/en";
      }
      return `/en${cleanPath.startsWith("/") ? cleanPath : `/${cleanPath}`}`;
    }
    
    // For Thai (default), return path without prefix
    return cleanPath || "/";
  };

  /**
   * Get the URL path for switching to a target language
   */
  const switchLanguagePath = (targetLang: "en" | "th"): string => {
    const currentPath = location.pathname;
    
    // Remove existing /en prefix
    const pathWithoutLang = currentPath.replace(/^\/en/, "") || "/";
    
    if (targetLang === "en") {
      if (pathWithoutLang === "/") {
        return "/en";
      }
      return `/en${pathWithoutLang}`;
    }
    
    // For Thai, return path without prefix
    return pathWithoutLang;
  };

  /**
   * Get language from URL path
   */
  const getLanguageFromPath = (path: string): "en" | "th" => {
    return path.startsWith("/en") ? "en" : "th";
  };

  /**
   * Check if current path is English
   */
  const isEnglishPath = (): boolean => {
    return location.pathname.startsWith("/en");
  };

  return {
    getLocalizedPath,
    switchLanguagePath,
    getLanguageFromPath,
    isEnglishPath,
  };
};

export default useLocalizedPath;
